<script setup lang="ts">
/**
 * Модальное окно с подробной информацией о продукте (мобильная версия)
 * Следует Vue Style Guide: Priority A
 */

import { computed } from 'vue';
import type { ProductKBJU } from '../model/types/product.types';
import { useBottomSheet, useScrollLock } from '~/shared/lib';

interface Props {
  modelValue: boolean;
  name: string;
  description?: string;
  amount?: number;
  volume?: number;
  weight?: number;
  kbju?: ProductKBJU;
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const isOpen = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
});

// Bottom sheet логика для свайпа
const {
  sheetRef,
  scrollInnerRef,
  handleTouchStart,
  handleTouchMove,
  handleTouchEnd,
} = useBottomSheet({
  onClose: () => emit('update:modelValue', false),
});

const close = () => {
  isOpen.value = false;
};

// Пока шторка открыта — фон не скроллится и не «прыгает».
useScrollLock(isOpen);

// Есть ли КБЖУ данные
const hasKbju = computed(() => {
  if (!props.kbju) return false;
  return !!(
    props.kbju.proteins ||
    props.kbju.fats ||
    props.kbju.carbohydrates ||
    props.kbju.calories
  );
});

// Есть ли измерения
const hasMeasurements = computed(() => {
  return !!(props.amount || props.volume || props.weight);
});
</script>

<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div
        v-if="isOpen"
        class="product-info-modal"
        @click="close"
      >
        <Transition name="modal-slide">
          <div
            v-if="isOpen"
            ref="sheetRef"
            class="product-info-modal__content"
            @click.stop
            @touchstart="handleTouchStart"
            @touchmove="handleTouchMove"
            @touchend="handleTouchEnd"
          >
            <!-- Заголовок с индикатором -->
            <div class="product-info-modal__header">
              <!-- Индикатор свайпа -->
              <div class="product-info-modal__handle">
                <div class="product-info-modal__handle-bar" />
              </div>
              <div class="product-info-modal__header-content">
                <h3 class="product-info-modal__title">О продукте</h3>
                <button
                  class="product-info-modal__close"
                  @click="close"
                >
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Контент (прокручиваемый) -->
            <div ref="scrollInnerRef" class="product-info-modal__scroll-content">
              <!-- Описание -->
              <div v-if="description" class="product-info-modal__description">
                <p>{{ description }}</p>
              </div>

              <!-- Измерения -->
              <div v-if="hasMeasurements" class="product-info-modal__measurements">
                <span v-if="amount">{{ amount }} шт.</span>
                <span v-if="volume">{{ volume }} мл.</span>
                <span v-if="weight">{{ weight }} г.</span>
              </div>

              <!-- КБЖУ -->
              <div v-if="hasKbju" class="product-info-modal__kbju">
                <div v-if="kbju?.proteins" class="product-info-modal__kbju-item">
                  <div class="product-info-modal__kbju-value">{{ kbju.proteins }}</div>
                  <div class="product-info-modal__kbju-label">Белки</div>
                </div>
                <div v-if="kbju?.fats" class="product-info-modal__kbju-item">
                  <div class="product-info-modal__kbju-value">{{ kbju.fats }}</div>
                  <div class="product-info-modal__kbju-label">Жиры</div>
                </div>
                <div v-if="kbju?.carbohydrates" class="product-info-modal__kbju-item">
                  <div class="product-info-modal__kbju-value">{{ kbju.carbohydrates }}</div>
                  <div class="product-info-modal__kbju-label">Углеводы</div>
                </div>
                <div v-if="kbju?.calories" class="product-info-modal__kbju-item">
                  <div class="product-info-modal__kbju-value">{{ kbju.calories }}</div>
                  <div class="product-info-modal__kbju-label">Калории</div>
                </div>
              </div>
            </div>

            <!-- Кнопка закрытия -->
            <UiKitButton
              color="default"
              size="lg"
              full-width
              class="product-info-modal__button"
              @click="close"
            >
              Понятно
            </UiKitButton>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
.product-info-modal {
  @apply fixed inset-0 flex items-end justify-center;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 53;

  @media (min-width: 768px) {
    display: none;
  }

  &__content {
    @apply w-full max-w-md bg-hui-content1 rounded-t-2xl p-6 max-h-[80vh] flex flex-col;
  }

  &__header {
    @apply flex flex-col mb-6;
  }

  &__handle {
    @apply flex justify-center mb-4;
  }

  &__handle-bar {
    @apply w-12 h-1 bg-hui-default-300 rounded-full;
  }

  &__header-content {
    @apply flex justify-between items-center;
  }

  &__scroll-content {
    @apply overflow-y-auto;
    max-height: calc(80vh - 200px);
  }

  &__title {
    @apply text-lg font-semibold text-hui-foreground;
  }

  &__close {
    @apply text-hui-foreground/50 hover:text-hui-foreground transition-colors;
  }

  &__description {
    @apply mb-4;

    p {
      @apply text-sm text-hui-foreground/60;
    }
  }

  &__measurements {
    @apply flex gap-3 mb-4 text-sm text-hui-foreground/60;
  }

  &__kbju {
    @apply flex justify-between gap-4 p-3 bg-hui-content2 text-hui-foreground rounded-hui-md mb-6;
  }

  &__kbju-item {
    @apply flex flex-col text-center text-base;
  }

  &__kbju-value {
    @apply font-medium;
  }

  &__kbju-label {
    @apply text-xs text-hui-foreground/60;
  }

  &__button {
    @apply mt-2;
  }
}

// Анимации
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-slide-enter-active,
.modal-slide-leave-active {
  transition: transform 0.3s ease-out;
}

.modal-slide-enter-from,
.modal-slide-leave-to {
  transform: translateY(100%);
}
</style>

